import { addSpinner, removeSpinner } from '../loading-spinner';
import { ModalVisibilityObserver } from '../modal-visibility-observer';
import {
    IDialogRegisteredEventDetail,
    ManagedDialogHandler
} from '../managed-dialog-handler';
import { shouldInitializeStoresModal } from './utils';
import { ZipcodeFormValidator } from './zipcode-form-validator';

interface IStoreControls {
    clearZipcodeButton: HTMLButtonElement | null;
    getLocationInstructions: HTMLElement | null;
    noLocationInstructions: HTMLElement | null;
    rangeSelect: HTMLSelectElement | null;
    storeListResult: HTMLElement | null;
    useLocationButton: HTMLButtonElement | null;
    zipcodeConfirmButton: HTMLButtonElement | null;
    zipcodeForm: HTMLFormElement | null;
    zipcodeInput: HTMLInputElement | null;
    zipcodeModal: HTMLDialogElement | null;
    zipcodeValue: string;
}

class StoresPage {
    private static readonly StoresListResult: string = 'stores-list-result';
    private static readonly StoresZipcodeDialogId: string =
        'storesZipcodeDialog';

    private managedDialogHandler!: ManagedDialogHandler;
    private zipcodeFormValidator!: ZipcodeFormValidator;

    // keep track of listeners so we can remove them via the abort signal.
    // this also makes testing things easier.
    private readonly listeners = new AbortController();

    constructor() {
        const opts = { signal: this.listeners.signal };

        addEventListener(
            ModalVisibilityObserver.ModalOpenedEventName,
            this.handleModalOpened.bind(this),
            opts
        );

        addEventListener(
            ManagedDialogHandler.DialogRegisteredEventName,
            this.handleDialogRegistration.bind(this),
            opts
        );

        document.addEventListener(
            'click',
            evt => {
                // we're doing this here because whenever htmx updates the DOM with new markup,
                // we can lose event listeners that were originally attached via the bundle, e.g. scripts.min.js.
                // this is a way to ensure the page will always be able to handle clicks on these buttons.
                const targetElement = evt.target as Element;
                if (targetElement.closest('#useLocationButton')) {
                    this.getLocation();
                } else if (targetElement.closest('#clearZipcodeButton')) {
                    this.clearZipcodeInput();
                }
            },
            opts
        );

        document.addEventListener(
            'htmx:beforeRequest',
            evt => {
                const targetElement = evt.target as Element;
                if (targetElement.closest('#zipCodeForm')) {
                    this.resetZipcodeModal();
                }
            },
            opts
        );

        document.addEventListener(
            'htmx:beforeHistorySave',
            () => {
                // we want to clear the spinner right before htmx:beforeHistorySave takes a snapshot
                // of the current page and stores it in the browser history cache. that way when the user navigates back
                // to this entry, it won't have the spinner in an infinite state.
                this.clearSpinnerBeforeSnapshot();
            },
            opts
        );
    }

    private getStoreControls(): IStoreControls {
        return {
            clearZipcodeButton:
                (document.getElementById(
                    'clearZipcodeButton'
                ) as HTMLButtonElement) ?? null,
            getLocationInstructions:
                (document.getElementById(
                    'getLocationInstructions'
                ) as HTMLElement) ?? null,
            noLocationInstructions:
                (document.getElementById(
                    'noLocationInstructions'
                ) as HTMLElement) ?? null,
            rangeSelect:
                (document.getElementById(
                    'range-select'
                ) as HTMLSelectElement) ?? null,
            storeListResult:
                (document.getElementById(
                    'stores-list-result'
                ) as HTMLElement) ?? null,
            useLocationButton:
                (document.getElementById(
                    'useLocationButton'
                ) as HTMLButtonElement) ?? null,
            zipcodeConfirmButton:
                (document.getElementById(
                    'zipcodeConfirmButton'
                ) as HTMLButtonElement) ?? null,
            zipcodeForm:
                (document.getElementById('zipCodeForm') as HTMLFormElement) ??
                null,
            zipcodeInput:
                (document.getElementById(
                    'storesModalInput'
                ) as HTMLInputElement) ?? null,
            zipcodeModal:
                (document.getElementById(
                    'storesZipcodeDialog'
                ) as HTMLDialogElement) ?? null,
            zipcodeValue:
                (
                    document.getElementById(
                        'storesModalInput'
                    ) as HTMLInputElement
                )?.value ?? ''
        } satisfies IStoreControls;
    }

    private handleDialogRegistration(event: Event): void {
        const dialogEvent = event as CustomEvent<IDialogRegisteredEventDetail>;

        if (dialogEvent.detail.dialogId === StoresPage.StoresZipcodeDialogId) {
            this.managedDialogHandler = dialogEvent.detail.dialogHandler;
            this.zipcodeFormValidator = new ZipcodeFormValidator();
        }
    }

    private handleModalOpened(): void {
        const { zipcodeModal, zipcodeInput } = this.getStoreControls();

        const zipcodeQueryParam: string | undefined = location.search
            .replace('?', '')
            .split('&')
            .find(param => param.startsWith('zipcode='));
        const zipcodeQueryParamValue: string = zipcodeQueryParam
            ? zipcodeQueryParam.split('=')[1]
            : '';

        if (
            zipcodeQueryParamValue !== '' &&
            zipcodeInput?.value !== zipcodeQueryParamValue
        ) {
            zipcodeInput && (zipcodeInput.value = zipcodeQueryParamValue);

            // Trigger the validation
            for (const zipcodeDigit of zipcodeQueryParamValue) {
                zipcodeInput?.dispatchEvent(
                    new KeyboardEvent('keyup', { key: zipcodeDigit })
                );
            }
        }

        zipcodeInput?.focus();
    }

    private resetZipcodeInput(): void {
        const { zipcodeConfirmButton, zipcodeInput } = this.getStoreControls();

        (window as any)['htmx'].removeClass(zipcodeInput, 'is-invalid');
        (window as any)['htmx'].removeClass(zipcodeInput, 'is-valid');

        zipcodeInput && (zipcodeInput.value = '');
    }

    private clearZipcodeInput(): void {
        const { zipcodeInput } = this.getStoreControls();

        this.resetZipcodeInput();
        this.zipcodeFormValidator?.validateZipcode();

        zipcodeInput?.focus();
    }

    private getLocation(): void {
        if (navigator.geolocation) {
            const {
                getLocationInstructions,
                noLocationInstructions,
                rangeSelect,
                useLocationButton
            } = this.getStoreControls();

            addSpinner(StoresPage.StoresListResult);

            navigator.geolocation.getCurrentPosition(
                function (position) {
                    const ajaxURL = `/stores/list?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}${rangeSelect ? '&range=' + rangeSelect.value : ''}`;
                    (window as any)['htmx']
                        .ajax('GET', ajaxURL, {
                            target: '#stores-list-result',
                            swap: 'innerHTML',
                            source: '#useLocationButton'
                        })
                        .finally(() => {
                            removeSpinner(StoresPage.StoresListResult);
                        });
                },
                function (error) {
                    removeSpinner(StoresPage.StoresListResult);
                    if (error.code === error.PERMISSION_DENIED) {
                        useLocationButton && (useLocationButton.hidden = true);
                        noLocationInstructions &&
                            (noLocationInstructions.hidden = false);
                        getLocationInstructions &&
                            (getLocationInstructions.hidden = true);
                    }
                }
            );
        }
    }

    private resetZipcodeModal(): void {
        this.resetZipcodeInput();
        this.zipcodeFormValidator?.validateZipcode();

        this.managedDialogHandler?.close();
    }

    private clearSpinnerBeforeSnapshot(): void {
        if (
            document
                .getElementById(StoresPage.StoresListResult)
                ?.hasAttribute('data-spinner-styles')
        ) {
            removeSpinner(StoresPage.StoresListResult);
        }
    }

    dispose(): void {
        this.listeners.abort();
    }
}

// htmx re-executes this bundle on every history-cache restore so we should replace the previous instance of the store page.
(function () {
    const w = window as { storesPage?: StoresPage };
    w.storesPage?.dispose();
    w.storesPage = undefined;
    const create = () => {
        if (shouldInitializeStoresModal()) w.storesPage = new StoresPage();
    };
    if (document.readyState !== 'complete')
        document.addEventListener('DOMContentLoaded', create);
    else create();
})();
